import React, { useState, useEffect, useCallback } from "react";
import { TextInput, Button, Alert, Badge, Loader } from "@mantine/core";
import {
  IconKey,
  IconUser,
  IconClock,
  IconAlertCircle,
  IconCheck,
  IconLink,
  IconBuilding,
  IconPlus,
  IconRefresh,
  IconCopy,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders, fetchAdminClients } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import AddClientModal from "../Client/AddClientModal";
import PageContent from "../shared/PageContent/PageContent";
import "../Organization/OrganizationPage.css";
import "./AdminPage.css";

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { id: "clients", label: "Clients", icon: IconBuilding },
  { id: "token", label: "Token Generator", icon: IconKey },
];

// ─── Client Course Card ───────────────────────────────────────────────────────
const ClientCourseCard = ({ company, onClick }) => {
  const getBadgeClass = status => {
    switch ((status || "active").toLowerCase()) {
      case "active":
        return "admin-client-card__badge--active";
      case "inactive":
        return "admin-client-card__badge--inactive";
      case "pending":
        return "admin-client-card__badge--pending";
      default:
        return "admin-client-card__badge--default";
    }
  };

  // Generate a deterministic gradient based on company name for placeholder
  const getPlaceholderGradient = name => {
    const gradients = [
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
      "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
      "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
      "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
      "linear-gradient(135deg, #fd7043 0%, #ff8a65 100%)",
    ];
    const idx = (name || "").charCodeAt(0) % gradients.length;
    return gradients[idx];
  };

  const initials = (company.name || "?")
    .split(" ")
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();

  return (
    <div
      className="admin-client-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      {/* Thumbnail area */}
      <div className="admin-client-card__thumbnail">
        {company.logo_url ? (
          <img
            src={company.logo_url}
            alt={company.name}
            loading="lazy"
            onError={e => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        {/* Placeholder gradient with initials */}
        <div
          className="admin-client-card__thumbnail-placeholder"
          style={{
            background: getPlaceholderGradient(company.name),
            display: company.logo_url ? "none" : "flex",
          }}
        >
          <span className="admin-client-card__thumbnail-initials">
            {initials}
          </span>
        </div>
        {/* Gradient overlay */}
        <div className="admin-client-card__gradient-overlay" />
        {/* Status badge overlay */}
        <div className="admin-client-card__overlay-badge">
          <span
            className={`admin-client-card__badge ${getBadgeClass(company.status)}`}
          >
            {company.status || "Active"}
          </span>
        </div>
      </div>

      {/* Card content */}
      <div className="admin-client-card__content">
        <h3 className="admin-client-card__name">
          {company.name || "Unnamed Company"}
        </h3>
        {company.industry && (
          <p className="admin-client-card__industry">{company.industry}</p>
        )}
        {company.email && !company.industry && (
          <p className="admin-client-card__industry">{company.email}</p>
        )}
      </div>
    </div>
  );
};

// ─── Clients Tab ──────────────────────────────────────────────────────────────
const ClientsTab = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalOpened, setAddModalOpened] = useState(false);
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      setClients(await fetchAdminClients(jwt));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // The code from the clients list identifies the company from here on - it's
  // what the admin pages carry in their URL and send as X-Company-Code.
  const handleCompanyClick = company => {
    setAdminCompanyCode(company.code);
    navigate(`/client/${company.code}`);
  };

  const handleCreateCompany = async name => {
    const jwt = await getVerifiedJWT();
    const response = await fetch(getAdminApiEndpoint("/v1/admin/client"), {
      method: "POST",
      headers: adminHeaders(jwt, ""),
      body: JSON.stringify({ name }),
    });
    if (!response.ok)
      throw new Error(`Failed to create company: ${response.status}`);
    fetchClients();
  };

  if (loading) {
    return (
      <div className="admin-empty">
        <Loader size="lg" />
        <p className="admin-empty__title">Loading clients…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error loading clients"
          color="red"
          mb="lg"
        >
          {error}
        </Alert>
        <Button
          leftSection={<IconRefresh size={14} />}
          onClick={fetchClients}
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Section header — matches training courses style */}
      <div className="admin-section__header">
        <div>
          <h2 className="admin-section__title">Clients</h2>
          <p className="admin-section__subtitle">
            {clients.length} {clients.length === 1 ? "company" : "companies"}{" "}
            registered
          </p>
        </div>
        <button
          className="admin-section__new-btn"
          onClick={() => setAddModalOpened(true)}
        >
          <IconPlus size={16} />
          Add Company
        </button>
      </div>

      {/* Grid */}
      {clients.length === 0 ? (
        <div className="admin-empty">
          <IconBuilding size={56} className="admin-empty__icon" />
          <p className="admin-empty__title">No clients yet</p>
          <p className="admin-empty__subtitle">
            Add your first company to get started.
          </p>
        </div>
      ) : (
        <div className="admin-clients__grid">
          {clients.map(company => (
            <ClientCourseCard
              key={company.id}
              company={company}
              onClick={() => handleCompanyClick(company)}
            />
          ))}
        </div>
      )}

      <AddClientModal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        onSave={handleCreateCompany}
      />
    </>
  );
};

// ─── Token Generator Tab ──────────────────────────────────────────────────────
const TokenTab = () => {
  const [repId, setRepId] = useState("");
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // "token" | "url" | null
  const { getVerifiedJWT } = useAuth();

  const handleGenerateToken = async () => {
    if (!repId.trim()) {
      setError("Please enter a rep ID");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setTokenData(null);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint("/v1/admin/generate-rep-token"),
        {
          method: "POST",
          headers: adminHeaders(jwt),
          body: JSON.stringify({ rep_id: repId.trim() }),
        }
      );
      if (!response.ok) {
        const result = await response.json();
        throw new Error(
          result.error || `Failed to generate token: ${response.status}`
        );
      }
      const result = await response.json();
      if (result.success && result.data) {
        setTokenData(result.data);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {}
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const loginUrl = tokenData
    ? `${window.location.origin}/admin-token/${tokenData.token}`
    : "";

  return (
    <>
      {/* Section header */}
      <div className="admin-section__header">
        <div>
          <h2 className="admin-section__title">Rep Token Generator</h2>
          <p className="admin-section__subtitle">
            Generate a temporary 5-minute authentication token for any rep
          </p>
        </div>
      </div>

      <div className="admin-token__card">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            mb="lg"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {tokenData ? (
          <div className="admin-token__result">
            {/* Success banner */}
            <Alert
              icon={<IconCheck size={16} />}
              title="Token Generated Successfully"
              color="green"
            >
              Token expires in {tokenData.expires_in}.
            </Alert>

            {/* Token block */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}
                >
                  Authentication Token
                </span>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={
                    copied === "token" ? (
                      <IconCheck size={12} />
                    ) : (
                      <IconCopy size={12} />
                    )
                  }
                  color={copied === "token" ? "green" : "blue"}
                  onClick={() => copyToClipboard(tokenData.token, "token")}
                >
                  {copied === "token" ? "Copied!" : "Copy Token"}
                </Button>
              </div>
              <div className="admin-token__code-block">{tokenData.token}</div>
            </div>

            {/* Login URL block */}
            <div className="admin-token__info-card admin-token__info-card--blue">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: 600,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <IconLink size={14} /> Login URL
                </span>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={
                    copied === "url" ? (
                      <IconCheck size={12} />
                    ) : (
                      <IconCopy size={12} />
                    )
                  }
                  color={copied === "url" ? "green" : "blue"}
                  onClick={() => copyToClipboard(loginUrl, "url")}
                >
                  {copied === "url" ? "Copied!" : "Copy URL"}
                </Button>
              </div>
              <div className="admin-token__code-block">{loginUrl}</div>
              <p
                style={{
                  fontSize: "var(--font-size-xs)",
                  color: "var(--color-text-secondary)",
                  margin: "8px 0 0",
                }}
              >
                Use this URL to log in directly with the generated token
              </p>
            </div>

            {/* User details */}
            {tokenData.user && (
              <div className="admin-token__info-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <IconUser size={16} />
                  <span
                    style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}
                  >
                    Representative
                  </span>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Rep ID</span>
                  <Badge variant="light">{tokenData.user.rep_id}</Badge>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Name</span>
                  <span className="admin-token__info-value">
                    {tokenData.user.first_name} {tokenData.user.last_name}
                  </span>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Email</span>
                  <span className="admin-token__info-value">
                    {tokenData.user.email}
                  </span>
                </div>
              </div>
            )}

            {/* Season details */}
            {tokenData.season && (
              <div className="admin-token__info-card">
                <div style={{ marginBottom: "12px" }}>
                  <span
                    style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}
                  >
                    Season
                  </span>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Season ID</span>
                  <Badge variant="light">{tokenData.season.id}</Badge>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Name</span>
                  <span className="admin-token__info-value">
                    {tokenData.season.name}
                  </span>
                </div>
                <div className="admin-token__info-row">
                  <span className="admin-token__info-label">Company ID</span>
                  <Badge variant="light">{tokenData.season.company_id}</Badge>
                </div>
              </div>
            )}

            {/* Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "4px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                }}
              >
                <IconClock size={14} /> Expires in: {tokenData.expires_in}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTokenData(null);
                  setError(null);
                  setRepId("");
                }}
              >
                Generate New Token
              </Button>
            </div>
          </div>
        ) : (
          <div className="admin-token__form">
            <TextInput
              label="Representative ID"
              placeholder="Enter rep ID"
              value={repId}
              onChange={e => setRepId(e.currentTarget.value)}
              required
              description="Enter the rep_id for the representative you want to generate a token for"
              onKeyDown={e => {
                if (e.key === "Enter") handleGenerateToken();
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                leftSection={
                  loading ? (
                    <Loader size={14} color="white" />
                  ) : (
                    <IconKey size={16} />
                  )
                }
                onClick={handleGenerateToken}
                disabled={!repId.trim() || loading}
              >
                {loading ? "Generating…" : "Generate Token"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Main AdminPage ───────────────────────────────────────────────────────────
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("clients");

  return (
    <PageContent
      customHeader={
        <div className="org-page-custom-header admin-page-header-wrap">
          {/* Left: title + tabs on the same row */}
          <div className="org-page-header-left">
            <h1 className="org-page-main-title">Admin</h1>
            <nav className="org-page-nav-tabs" role="tablist">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`org-page-nav-tab${activeTab === tab.id ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      }
    >
      {activeTab === "clients" && <ClientsTab />}
      {activeTab === "token" && <TokenTab />}
    </PageContent>
  );
};

export default AdminPage;
